import type { ICommand } from "./ICommand";
import type { DRDrawingViewer } from "../DRDrawingViewer";
import type { DrawingModule } from "../types/DrawingWeb";
import type { Point3D } from "../draggers/types";
import { deleteObj } from "../core/delete";
import { openAs, getEntityHandle, findEntityByHandle } from "../core/oda";
import { OpenMode } from "../core/constants";
import { oplogEmitter, type OplogEmit } from "../meta/oplog";

export interface AddPointToPolylineParams {
  /** The OdDbObjectId of the polyline entity */
  entityId: DrawingModule.OdDbObjectId;
  /** The point to add (world coordinates) */
  point: Point3D;
  /** The vertex index where to add the point */
  vertexIndex: number;
}

/**
 * Command for adding a point to an existing polyline.
 * Used for incremental polyline drawing with undo support.
 */
export class AddPointToPolylineCommand implements ICommand {
  private viewer: DRDrawingViewer;
  private handle: string;
  private point: Point3D;
  private vertexIndex: number;
  private oplogEvents: OplogEmit[] = [];

  constructor(viewer: DRDrawingViewer, params: AddPointToPolylineParams) {
    this.viewer = viewer;
    this.handle = getEntityHandle(params.entityId);
    this.point = params.point;
    this.vertexIndex = params.vertexIndex;
  }

  /** Add the vertex to the polyline */
  execute(): void {
    this.addVertex();
    this.oplogEvents = oplogEmitter.emitModified(this.viewer, [
      { handle: this.handle, changeKind: "extended" },
    ]);
    this.viewer.update();
  }

  /** Remove the vertex from the polyline */
  undo(): void {
    const polyline = this.openPolyline(OpenMode.kForWrite);
    if (!polyline) return;

    polyline.removeVertexAt(this.vertexIndex);
    // Without this, the WASM renderer's cached graphics for this entity
    // aren't invalidated — the removed vertex stays visible on screen until
    // something else (e.g. a zoom) forces a full repaint.
    polyline.recordGraphicsModified(true);
    deleteObj(polyline);

    oplogEmitter.revert(this.oplogEvents);
    this.viewer.update();
  }

  /** Re-add the vertex — pas de ré-émission (reemit réutilise l'event existant). */
  redo(): void {
    this.addVertex();
    oplogEmitter.reemit(this.oplogEvents);
    this.viewer.update();
  }

  private addVertex(): void {
    const polyline = this.openPolyline(OpenMode.kForWrite);
    if (!polyline) return;

    const module = this.viewer.module;
    const point2d = new module.OdGePoint2d(this.point.x, this.point.y);
    polyline.addVertexAt(this.vertexIndex, point2d, 0, 0, 0, 0);
    // See the comment in undo() — needed so the added vertex renders
    // immediately, on both the initial execute() and a later redo().
    polyline.recordGraphicsModified(true);
    deleteObj(point2d);
    deleteObj(polyline);
  }

  /** No special cleanup needed */
  dispose(): void {
    // Nothing to dispose - the entity is managed by AddEntityCommand
  }

  private openPolyline(
    openMode: DrawingModule.OpenMode,
  ): DrawingModule.OdDbPolyline | null {
    const entityId = findEntityByHandle(
      this.viewer.module,
      this.viewer.database,
      this.handle,
    );
    if (!entityId) return null;
    const polyline = openAs(
      entityId,
      this.viewer.module.OdDbPolyline,
      openMode,
    );
    deleteObj(entityId);
    return polyline;
  }
}
